/* import __COLOCATED_TEMPLATE__ from './impersonated-user-search-modal-content.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import copyToClipboard from 'copy-to-clipboard';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';
import type Session from 'embercom/services/session';
import type NotificationsService from 'embercom/services/notifications-service';
import type InboxApi from 'embercom/services/inbox-api';
import type Store from '@ember-data/store';
// @ts-ignore
import { copy } from 'ember-copy';
// @ts-ignore
import PredicateGroup from 'predicates/models/predicate-group';
import UserSummary from 'embercom/objects/inbox/user-summary';
import CompanySummary from 'embercom/objects/inbox/company-summary';
import GeoipData from 'embercom/objects/inbox/geoip-data';
import NavigableSelection from 'embercom/components/inbox2/common/navigable-selection-resource';
import { useResource } from 'ember-resources';
import { enqueueTask } from 'ember-concurrency-decorators';
import { restartableTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';

interface ImpersonatedUserSearchModalContentArgs {
  isOpen: boolean;
  user: UserSummary | null;
  onSelect: (user: UserSummary | null) => void;
  ruleset: $TSFixMe;
  onUpdatePredicates: (predicates: $TSFixMe) => void;
  onUpdateRolePredicates: (predicates: $TSFixMe) => void;
  onModalClose: () => void;
  modal: $TSFixMe;
}

export default class ImpersonatedUserSearchModalContent extends Component<ImpersonatedUserSearchModalContentArgs> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare session: Session;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;

  @service declare inboxApi: InboxApi;
  @service declare store: Store;
  @tracked userResults: Array<$TSFixMe> = []; // Array<models/user>
  @tracked currentPage = 1;

  @tracked query = '';

  @tracked searchRuleset: $TSFixMe;

  @tracked selection = useResource(this, NavigableSelection, () => ({
    items: this.userResults,
  }));

  constructor(owner: any, args: ImpersonatedUserSearchModalContentArgs) {
    super(owner, args);

    this.searchRuleset = this.store.createRecord('matching-system/ruleset');
    this.searchRuleset.set('predicateGroup', copy(this.args.ruleset.predicateGroup));
    this.searchRuleset.set('rolePredicateGroup', copy(this.args.ruleset.rolePredicateGroup));
    this.searchRuleset.set('defaultPredicateGroup', copy(this.args.ruleset.defaultPredicateGroup));
    this.updateQuery(this.query);
  }

  @action onUpdatePredicates(predicates: $TSFixMe) {
    this.args.onUpdatePredicates(predicates);
    this.updateQuery(this.query);
  }

  @action onUpdateRolePredicates(predicates: $TSFixMe) {
    this.args.onUpdateRolePredicates(predicates);
    this.updateQuery(this.query);
  }

  get isLoadingInitialResults() {
    return taskFor(this.loadResultsForNewSearch).isRunning;
  }

  @action openUserProfile(workspaceId: string, userId: string, event: MouseEvent) {
    event.stopPropagation();
    let url = this.router.urlFor('apps.app.users.user', workspaceId, userId);
    safeWindowOpen(url, '_blank');
  }

  @action copyUserId(userId: string, event: MouseEvent) {
    event.stopPropagation();
    copyToClipboard(userId, {
      format: 'text/plain',
      onCopy: () => {
        this.notificationsService.notifyConfirmation(
          this.intl.t('inbox.new-conversation.recipient-selector-tooltip.user-id-copy-success'),
        );
      },
    });
  }

  @action onInput(event: Event & { target: HTMLInputElement }) {
    this.updateQuery(event.target.value);
  }

  @action
  updateQuery(query: string) {
    this.query = query;
    this.currentPage = 1;
    this.userResults = [];
    let mainArgPredicates = this.args.ruleset.predicateGroup.predicates.serialize();
    let predicatesWithSearch =
      query.length > 0
        ? [
            ...mainArgPredicates,
            {
              type: 'or',
              predicates: [
                { attribute: 'name', comparison: 'contains', type: 'string', value: query },
                { attribute: 'email', comparison: 'contains', type: 'string', value: query },
                { attribute: 'user_id', comparison: 'eq', type: 'string', value: query },
              ],
            },
          ]
        : mainArgPredicates;
    this.searchRuleset.set(
      'predicateGroup',
      this.store.createFragment('predicates/predicate-group', {
        predicates: PredicateGroup.convertRawPredicates(this.store, predicatesWithSearch),
      }),
    );
    this.searchRuleset.set('rolePredicateGroup', copy(this.args.ruleset.rolePredicateGroup));
    taskFor(this.loadResultsForNewSearch).perform();
  }

  @action onLoadMore() {
    taskFor(this.loadResultsPage).perform(this.currentPage);
  }

  get nrRealResults() {
    if (this.userResults.length > 0 && this.userResults[0].id === 'preview-user') {
      return this.userResults.length - 1;
    }
    return this.userResults.length;
  }

  get isLoadingPage() {
    return taskFor(this.loadResultsPage).isRunning;
  }

  get noMoreResults() {
    // subtract 1 because we have a preview user
    return this.nrRealResults === this.searchRuleset.audiencePreview?.count;
  }

  get noResults() {
    return this.nrRealResults === 0 && !this.isLoadingPage;
  }

  @restartableTask
  *loadResultsForNewSearch(): TaskGenerator<void> {
    yield this.searchRuleset.fetchAudiencePreviewTask.perform([]);
    let newResults = this.searchRuleset.audiencePreview.users.map((u: $TSFixMe) =>
      this.userModelToUserSummary(u.user),
    );

    let roles = this.searchRuleset.rolePredicateGroup.predicates.firstObject?.serialize().value;
    let isDefaultSearch =
      roles.includes('user_role') &&
      roles.includes('contact_role') &&
      this.searchRuleset.predicateGroup.predicates.length === 0 &&
      this.query.length === 0;

    this.userResults = [
      ...(isDefaultSearch ? [new UserSummary('preview-user')] : []),
      ...newResults,
    ];
    this.currentPage = 2;
  }

  @enqueueTask
  *loadResultsPage(page: number): TaskGenerator<void> {
    if (this.noMoreResults) {
      return;
    }
    yield this.searchRuleset.fetchAudiencePreviewTask.perform([], page);
    let newResults = this.searchRuleset.audiencePreview.users.map((u: $TSFixMe) =>
      this.userModelToUserSummary(u.user),
    );

    this.userResults = [...this.userResults, ...newResults];
    this.currentPage++;
  }

  userModelToUserSummary(user: $TSFixMe /* models/user */) {
    let companies = user.companies?.map(
      (c: $TSFixMe) =>
        new CompanySummary({ id: c.id, name: c.name, remote_company_id: c.remove_company_id }),
    );
    let companyIds = companies?.map((c: $TSFixMe) => c.id);
    let company = companies?.[0];
    let geoipData = user.geoip_data
      ? new GeoipData(
          user.geoip_data.city_name,
          user.geoip_data.country_code2,
          user.geoip_data.country_name,
          user.geoip_data.region_code,
          user.geoip_data.timezone,
        )
      : undefined;

    return new UserSummary(
      user.id,
      user.email,
      user.name,
      companyIds,
      company,
      companies,
      geoipData,
      // merged_into_id left as undefined as grabbing that means fetching the user record again, and it's not relevant here (see the app/models/user.js file)
      undefined,
      user.role,
      user.image_url,
      user.user_id,
      user.pseudonym,
      user.phone,
      user.phone_country,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Impersonation::ImpersonatedUserSearchModalContent': typeof ImpersonatedUserSearchModalContent;
  }
}
